import getConfig from 'next/config';
import { toCamelCaseDeep } from 'src/utils/transform';
import * as Types from './types';

const { publicRuntimeConfig } = getConfig();

const TAVUS_API_KEY = publicRuntimeConfig.TAVUS_API_KEY;
const TAVUS_API_BASE_URL = 'https://tavusapi.com';
const DEFAULT_PERSONA_ID = 'pa074c5919fd';
const DEFAULT_REPLICA_ID = 'rf4703150052';
const DEFAULT_CONVERSATION_NAME = 'Practicing with your Spanish tutor';

export default class AvatarTutorApiClass {
  private async tavusApiFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${TAVUS_API_BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        'x-api-key': TAVUS_API_KEY,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          errorData.error ||
          `Tavus API error: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return toCamelCaseDeep(data) as T;
  }

  public async createConversation(
    request: Partial<Types.CreateConversationRequest>,
  ): Promise<Types.CreateConversationResponse> {
    const conversationRequest: Types.CreateConversationRequest = {
      replica_id: request.replica_id || DEFAULT_REPLICA_ID,
      persona_id: request.persona_id || DEFAULT_PERSONA_ID,
      conversation_name: request.conversation_name || DEFAULT_CONVERSATION_NAME,
      conversational_context: request.conversational_context,
      callback_url: request.callback_url,
      properties: {
        max_call_duration: 240,
        language: 'Spanish',
      },
    };

    return this.tavusApiFetch<Types.CreateConversationResponse>('/v2/conversations', {
      method: 'POST',
      body: JSON.stringify(conversationRequest),
    });
  }

  public async getConversation(conversationId: string): Promise<Types.ConversationDetails> {
    return this.tavusApiFetch<Types.ConversationDetails>(`/v2/conversations/${conversationId}`);
  }

  public async endConversation(conversationId: string): Promise<void> {
    await this.tavusApiFetch(`/v2/conversations/${conversationId}/end`, {
      method: 'POST',
    });
  }

  public async listConversations(): Promise<{ conversations: Types.ConversationDetails[] }> {
    return this.tavusApiFetch<{ conversations: Types.ConversationDetails[] }>('/v2/conversations');
  }
}

export const AvatarTutorApi = new AvatarTutorApiClass();
